import React, { useEffect, useState, useCallback } from "react";
import { api } from "@/lib/api";
import { Link } from "react-router-dom";
import { Plus, X, ArrowRight, Download } from "lucide-react";

const STAGE_BADGE = {
  "New": "pgm-badge",
  "Booked": "pgm-badge",
  "Form Sent": "pgm-badge-orange",
  "Form Complete": "pgm-badge-magenta",
  "Promo Ready": "pgm-badge-olive",
  "Recorded": "pgm-badge-wine",
  "Published": "pgm-badge-olive",
  "Done": "pgm-badge-olive",
};

const NEXT_ACTIONS = [
  "Confirm booking",
  "Send confirmation email",
  "Send intake form",
  "Waiting on intake form",
  "Send 5-day form reminder",
  "Generate promo assets",
  "Add join link",
  "Email swipe copy & graphic",
  "Upload recording & clips",
  "Publish episode",
  "Add episode links",
  "Send thank-you email",
];

function nextAction(checklist) {
  const cl = checklist || [];
  for (let i = 0; i < NEXT_ACTIONS.length; i++) {
    if (i === 4 && cl[3]) continue;
    if (!cl[i]) return NEXT_ACTIONS[i];
  }
  return null;
}

// Email delivery at a glance: red = bouncing, green = last email confirmed delivered,
// amber = accepted but not confirmed yet, hollow = nothing tracked / no address.
function DeliveryDot({ guest }) {
  const d = guest.email_delivery;
  const when = (iso) => {
    if (!iso) return "";
    try { return new Date(iso).toLocaleDateString(undefined, { month: "short", day: "numeric" }); }
    catch (e) { return ""; }
  };
  let state = "none";
  let title = "No email tracked for this guest yet.";
  if (!((guest.email || "").trim())) {
    title = "No email address on this guest, so nothing can be sent.";
  } else if (guest.email_bounced) {
    state = "bad";
    title = `Email bouncing — "${guest.email_bounce_template || "email"}" never arrived (${guest.email_bounce_reason || "the address rejected it"}). Automated emails are paused for this guest.`;
  } else if (d) {
    const s = d.status;
    if (s === "delivered" || s === "opened" || s === "clicked") {
      state = "ok";
      title = `Last email delivered — ${d.label || "email"}${d.at ? `, ${when(d.at)}` : ""}.`;
    } else if (["bounced", "complained", "failed", "suppressed", "canceled"].includes(s)) {
      state = "bad";
      title = `Last email ${s} — ${d.reason || "the mail server rejected it"}.`;
    } else if (s === "delivery_delayed") {
      state = "warn";
      title = `Last email delayed at the guest's mail server — ${d.label || "email"}${d.at ? `, ${when(d.at)}` : ""}. Resend is still retrying.`;
    } else {
      state = "warn";
      title = `Last email accepted by Resend, delivery not confirmed yet — ${d.label || "email"}${d.at ? `, ${when(d.at)}` : ""}.`;
    }
  }
  const colour = { ok: "var(--pgm-olive)", warn: "var(--pgm-orange)", bad: "#B3261E", none: "transparent" }[state];
  return (
    <span
      title={title}
      aria-label={title}
      data-testid={`guest-delivery-dot-${guest.id}`}
      data-state={state}
      style={{
        display: "inline-block", verticalAlign: "middle", marginRight: 7, marginBottom: 2,
        width: 10, height: 10, borderRadius: "50%",
        background: colour,
        border: state === "none" ? "1.5px solid var(--pgm-rule)" : `1.5px solid ${colour}`,
        boxShadow: state === "bad" ? "0 0 0 3px rgba(179,38,30,0.18)" : "none",
      }}
    />
  );
}


export default function Guests() {
  const [guests, setGuests] = useState([]);
  const [open, setOpen] = useState(false);
  const [filter, setFilter] = useState("");
  const [hideCancelled, setHideCancelled] = useState(() => {
    // Default: hide cancelled unless the user has explicitly changed it
    const saved = typeof window !== "undefined" ? window.localStorage.getItem("pgm_hide_cancelled") : null;
    return saved === null ? true : saved === "1";
  });
  const [form, setForm] = useState({ full_name: "", email: "", phone: "", whatsapp: "", title: "", episode_date: "", episode_time: "", episode_number: "" });
  const [saving, setSaving] = useState(false);
  const [notice, setNotice] = useState(null);

  const load = useCallback(() => api.get("/guests").then(r => setGuests(r.data)), []);
  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    try { window.localStorage.setItem("pgm_hide_cancelled", hideCancelled ? "1" : "0"); } catch (e) { console.warn("Persist hideCancelled failed:", e); }
  }, [hideCancelled]);

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const { data } = await api.post("/guests", form);
      const w = data.welcome_email || {};
      if (w.sent) setNotice({ kind: "ok", text: `${data.full_name} added — confirmation + intake form emailed to ${data.email}.` });
      else if (w.skipped) setNotice({ kind: "warn", text: `${data.full_name} added without an email address — send the confirmation + intake form from their Emails tab when you have it.` });
      else setNotice({ kind: "warn", text: `${data.full_name} added, but the confirmation email failed (${w.reason || "unknown"}) — send it manually from their Emails tab.` });
      setOpen(false);
      setForm({ full_name: "", email: "", phone: "", whatsapp: "", title: "", episode_date: "", episode_time: "", episode_number: "" });
      load();
    } finally { setSaving(false); }
  };

  const cancelledCount = guests.filter(g => g.cancelled).length;
  const filtered = guests
    .filter(g => !hideCancelled || !g.cancelled)
    .filter(g => g.full_name?.toLowerCase().includes(filter.toLowerCase()) || g.title?.toLowerCase().includes(filter.toLowerCase()));

  const exportCsv = async () => {
    const res = await api.get("/exports/guests", { responseType: "blob" });
    const url = window.URL.createObjectURL(res.data);
    const a = document.createElement("a");
    a.href = url; a.download = "guests.csv"; document.body.appendChild(a); a.click(); a.remove();
    window.URL.revokeObjectURL(url);
  };

  return (
    <div className="pgm-reveal">
      <div className="pgm-micro">◆ Guest roster</div>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginTop: 4 }}>
        <h1 className="pgm-display" style={{ fontSize: 44 }}>Guests</h1>
        <div style={{ display: "flex", gap: 10 }}>
          <button onClick={exportCsv} className="pgm-btn pgm-btn-ghost" data-testid="export-guests-btn"><Download size={14} /> Export CSV</button>
          <button onClick={() => setOpen(true)} className="pgm-btn" data-testid="add-guest-btn">
            <Plus size={16} /> Add guest
          </button>
        </div>
      </div>

      {notice && (
        <div className="pgm-callout" style={{ marginTop: 16, borderLeftColor: notice.kind === "ok" ? "var(--pgm-olive)" : "var(--pgm-orange)", display: "flex", justifyContent: "space-between", alignItems: "center", gap: 12 }} data-testid="guest-added-notice">
          <span>{notice.text}</span>
          <button onClick={() => setNotice(null)} className="pgm-btn pgm-btn-ghost" style={{ padding: "4px 10px", fontSize: 10 }} data-testid="guest-added-notice-dismiss"><X size={11} /></button>
        </div>
      )}

      <div style={{ display: "flex", gap: 12, marginTop: 18, marginBottom: 16, alignItems: "center", flexWrap: "wrap" }}>
        <input
          placeholder="Search guests..."
          value={filter}
          onChange={(e) => setFilter(e.target.value)}
          className="pgm-input"
          style={{ maxWidth: 320, flex: "1 1 240px" }}
          data-testid="guests-search"
        />
        <label
          style={{ display: "inline-flex", alignItems: "center", gap: 8, fontSize: 12.5, color: "var(--pgm-wine)", cursor: cancelledCount ? "pointer" : "default", opacity: cancelledCount ? 1 : 0.55, whiteSpace: "nowrap" }}
          data-testid="hide-cancelled-toggle"
        >
          <input
            type="checkbox"
            checked={hideCancelled}
            onChange={(e) => setHideCancelled(e.target.checked)}
            disabled={!cancelledCount}
            data-testid="hide-cancelled-checkbox"
          />
          Hide cancelled{cancelledCount ? ` (${cancelledCount})` : ""}
        </label>
        <span style={{ display: "inline-flex", alignItems: "center", gap: 12, fontSize: 11.5, color: "var(--pgm-wine)", opacity: 0.8, flexWrap: "wrap" }} data-testid="delivery-dot-legend">
          <span style={{ display: "inline-flex", alignItems: "center", gap: 5 }}>
            <span style={{ width: 9, height: 9, borderRadius: "50%", background: "var(--pgm-olive)" }} /> Email delivered
          </span>
          <span style={{ display: "inline-flex", alignItems: "center", gap: 5 }}>
            <span style={{ width: 9, height: 9, borderRadius: "50%", background: "var(--pgm-orange)" }} /> Not confirmed yet
          </span>
          <span style={{ display: "inline-flex", alignItems: "center", gap: 5 }}>
            <span style={{ width: 9, height: 9, borderRadius: "50%", background: "#B3261E" }} /> Bouncing
          </span>
        </span>
      </div>

      {filtered.length === 0 ? (
        <div className="pgm-callout">
          <strong>No guests found.</strong> Click "Add guest" to create your first booking.
        </div>
      ) : (
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(360px, 1fr))", gap: 14 }} className="pgm-reveal-stagger">
          {filtered.map(g => {
            const done = (g.checklist || []).filter(Boolean).length;
            const next = nextAction(g.checklist);
            return (
              <Link to={`/guests/${g.id}`} key={g.id} className="pgm-card" style={{ textDecoration: "none", display: "block" }} data-testid={`guest-card-${g.id}`}>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
                  <div style={{ flex: 1 }}>
                    <div className="pgm-display" style={{ fontSize: 22, lineHeight: 1.2 }}>
                      <DeliveryDot guest={g} />
                      {g.full_name || "Untitled"}
                    </div>
                    <div style={{ fontSize: 13, color: "var(--pgm-wine)", opacity: 0.75, marginTop: 2 }}>{g.title || "—"}</div>
                  </div>
                  <span className={`pgm-badge ${STAGE_BADGE[g.stage] || "pgm-badge"}`}>{g.stage}</span>
                  {!g.cancelled && !g.no_show && (
                    <span
                      className="pgm-badge"
                      style={g.confirmed
                        ? { background: "var(--pgm-olive)", color: "var(--pgm-cream)" }
                        : { background: "transparent", color: "var(--pgm-orange)", border: "1px solid var(--pgm-orange)" }}
                      data-testid={`guest-confirmed-badge-${g.id}`}
                    >
                      {g.confirmed
                        ? `CONFIRMED${g.confirmed_at ? ` ${new Date(g.confirmed_at).toLocaleDateString(undefined, { month: "short", day: "numeric" })}` : ""}`
                        : "NOT CONFIRMED"}
                    </span>
                  )}
                  {g.no_show && (
                    <span className="pgm-badge" style={{ background: "#B3261E", color: "var(--pgm-cream)" }} data-testid={`guest-no-show-badge-${g.id}`}>
                      NO-SHOW
                    </span>
                  )}
                  {(g.episode_group_id || "").trim() && (
                    <span className="pgm-badge" style={{ background: "transparent", color: "var(--pgm-wine)", border: "1px solid var(--pgm-wine)" }} data-testid={`guest-panel-badge-${g.id}`}>
                      PANEL EPISODE
                    </span>
                  )}
                  {g.cancelled && (
                    <span
                      className="pgm-badge"
                      style={{ background: "var(--pgm-orange)", color: "var(--pgm-cream)" }}
                      data-testid={`guest-cancelled-badge-${g.id}`}
                    >
                      CANCELLED
                    </span>
                  )}
                </div>
                <div style={{ display: "flex", gap: 16, fontSize: 11, color: "var(--pgm-wine)", opacity: 0.7, marginTop: 8, textTransform: "uppercase", letterSpacing: "0.08em" }}>
                  <span>EP {g.episode_number || "—"}</span>
                  <span>{g.episode_date || "Date TBD"}</span>
                  <span>{done}/12 done</span>
                </div>
                <div className="pgm-progress" style={{ marginTop: 12 }}>
                  {Array.from({ length: 12 }).map((_, i) => (
                    <div key={`seg-${g.id}-${i}`} className={`pgm-progress-seg ${(g.checklist || [])[i] ? "done" : ""} ${i < 8 ? "pre" : ""}`} />
                  ))}
                </div>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginTop: 10 }}>
                  <span style={{ fontSize: 12, fontWeight: 700, color: next ? "var(--pgm-wine)" : "var(--pgm-olive)" }} data-testid={`guest-next-step-${g.id}`}>
                    {next ? <>Next: <span style={{ color: "var(--pgm-magenta)" }}>{next}</span></> : "✓ All steps complete"}
                  </span>
                  <span style={{ display: "flex", alignItems: "center", gap: 4, color: "var(--pgm-magenta)", fontSize: 12, fontWeight: 700, letterSpacing: "0.08em" }}>
                    OPEN <ArrowRight size={14} />
                  </span>
                </div>
              </Link>
            );
          })}
        </div>
      )}

      {open && (
        <Modal onClose={() => setOpen(false)} title="Add a guest">
          <form onSubmit={submit} style={{ display: "flex", flexDirection: "column", gap: 12 }}>
            <div><label className="pgm-label">Full name *</label><input className="pgm-input" required value={form.full_name} onChange={e => setForm({ ...form, full_name: e.target.value })} data-testid="add-guest-name" /></div>
            <div><label className="pgm-label">Email</label><input className="pgm-input" type="email" value={form.email} onChange={e => setForm({ ...form, email: e.target.value })} data-testid="add-guest-email" /></div>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
              <div><label className="pgm-label">Mobile number</label><input className="pgm-input" placeholder="+1 555 123 4567" value={form.phone} onChange={e => setForm({ ...form, phone: e.target.value })} data-testid="add-guest-phone" /></div>
              <div><label className="pgm-label">WhatsApp number</label><input className="pgm-input" placeholder="+1 555 123 4567" value={form.whatsapp} onChange={e => setForm({ ...form, whatsapp: e.target.value })} data-testid="add-guest-whatsapp" /></div>
            </div>
            <div><label className="pgm-label">Title / role</label><input className="pgm-input" value={form.title} onChange={e => setForm({ ...form, title: e.target.value })} data-testid="add-guest-title" /></div>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
              <div><label className="pgm-label">Episode number</label><input className="pgm-input" placeholder="e.g. TSL001" value={form.episode_number} onChange={e => setForm({ ...form, episode_number: e.target.value })} data-testid="add-guest-epnum" /></div>
              <div><label className="pgm-label">Episode date</label><input className="pgm-input" type="date" value={form.episode_date} onChange={e => setForm({ ...form, episode_date: e.target.value })} data-testid="add-guest-epdate" /></div>
            </div>
            <div>
              <label className="pgm-label">Recording time (your show timezone)</label>
              <input className="pgm-input" type="time" value={form.episode_time} onChange={e => setForm({ ...form, episode_time: e.target.value })} data-testid="add-guest-eptime" />
              <div style={{ fontSize: 11, opacity: 0.65, marginTop: 4 }}>
                Used in every email and on the calendar invite that goes out with the booking confirmation.
              </div>
            </div>
            <button type="submit" disabled={saving} className="pgm-btn" data-testid="add-guest-submit">{saving ? "Saving..." : "Add guest"}</button>
            <button type="button" className="pgm-btn pgm-btn-ghost" onClick={() => setOpen(false)} data-testid="add-guest-cancel">Cancel</button>
          </form>
        </Modal>
      )}
    </div>
  );
}

export function Modal({ onClose, title, children, width = 520 }) {
  return (
    <div
      onClick={onClose}
      className="pgm-modal-backdrop"
      style={{ position: "fixed", inset: 0, background: "rgba(78,29,94,0.55)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 50, padding: 20 }}
      data-testid="modal-backdrop"
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="pgm-modal-panel pgm-reveal"
        style={{ background: "var(--pgm-paper)", border: "1.5px solid var(--pgm-wine)", borderLeft: "4px solid var(--pgm-magenta)", padding: 24, width: "100%", maxWidth: width, maxHeight: "90vh", overflow: "auto" }}
      >
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 12 }}>
          <h2 className="pgm-display" style={{ fontSize: 24 }}>{title}</h2>
          <button
            onClick={onClose}
            className="pgm-modal-close"
            style={{ border: "none", background: "transparent", cursor: "pointer", color: "var(--pgm-wine)", display: "inline-flex", alignItems: "center", justifyContent: "center" }}
            data-testid="modal-close"
            aria-label="Close"
          >
            <X size={22} />
          </button>
        </div>
        {children}
      </div>
    </div>
  );
}
